import { useCallback, useMemo, useEffect, useRef } from "react";
import { SyncLoader } from "react-spinners";
import ThemeConstants from "../../../../config/theme/theme_constants";
import SurveyCardElement from "./survey_card_element";
import { useGetSurveys } from "../../domain/hooks/use_get_surveys";
import UvibesPointIcon from '../../../../assets/images/uvibes_point.svg?react';
import { useTranslation } from '../../../../i18n/useTranslation';

const theme = ThemeConstants();
const COLORS = [
    theme.UvibesYellow,
    theme.UvibesGreen,
    theme.UvibesBlue,
    theme.UvibesOrange,
    theme.UvibesPink,
];

const SurveyList: React.FC = () => {
    const { t } = useTranslation();
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const {
        isPending,
        allSurveys,
        error,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useGetSurveys();

    const handleLoadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        const el = loadMoreRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    handleLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(el);

        return () => observer.disconnect();
    }, [handleLoadMore, hasNextPage]);

    const totalPointsToWin = useMemo(() => {
        let totalPoints = 0;
        for (const survey of allSurveys) {
            totalPoints += survey.nbPointsLeft;
        }
        return totalPoints;
    }, [allSurveys]);

    return (
        <div className="flex flex-col gap-3 max-w-94.5 w-full">
            <div className="flex items-center gap-3">
                <span className="font-bold">{t('survey.list.points_left')}</span>
                <div className="flex items-center justify-center gap-1.25 bg-white py-1.25 px-2.5 rounded-full min-w-16.5">
                    {isPending
                        ? <div className="animate-spin w-3 h-3 border-2 border-primary border-t-transparent rounded-full my-1 mx-4.25"/>
                        : <>
                            <span className="text-xs font-semibold">{`+${totalPointsToWin}`}</span>
                            <UvibesPointIcon className="h-5 w-5"/>
                        </>
                    }
                </div>
            </div>

            {error
                ? <div className="flex flex-col items-center gap-2">
                    <p className="text-sm text-center">{t('survey.list.fetch_error')}</p>
                    <button
                        className="px-4 py-2 rounded-[18px] text-white font-semibold text-sm cursor-pointer"
                        style={{ background: theme.UvibesPink }}
                        onClick={() => refetch()}
                        >
                        {t('common.button.retry')}
                    </button>
                </div> : allSurveys && allSurveys.length > 0 ? allSurveys!.map((survey, index) => (
                    <SurveyCardElement 
                        key={survey.id} 
                        survey={survey} 
                        color={COLORS[index % COLORS.length]}
                    />
                )) : <p className="text-center">{t('survey.list.empty')}</p>

            }
            
            {hasNextPage && (
                <div ref={loadMoreRef} className="flex justify-center py-4">
                    {isFetchingNextPage ? (
                        <SyncLoader color={theme.UvibesPink} size={16} speedMultiplier={0.5}/>
                    ) : (
                        <button 
                            onClick={handleLoadMore}
                            className="text-sm font-medium px-6 py-2 rounded-full border border-gray-300 hover:bg-gray-50"
                        >
                            {t('survey.list.load_more')}
                        </button>
                    )}
                </div>
            )}
            
            <SyncLoader color="black" className="flex justify-center pt-4" loading={isPending} size={16} speedMultiplier={0.5}/>
        </div>
    );
}

export default SurveyList;
